/**
 * Word Count Tool
 *
 * Counts words, characters, lines, paragraphs, sentences, and estimates reading time.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

export const WordCountTool: ITransformerTool = {
  id: 'word-count',
  name: 'Word Count',
  description: 'Count words, characters, lines, paragraphs, sentences, and reading time',
  version: '1.0.0',
  type: 'transformer',
  category: 'Text Tools',
  keywords: ['word', 'count', 'character', 'line', 'paragraph', 'reading time', 'text'],

  seo: {
    slug: 'word-counter',
    pageTitle: 'Word Counter - Free Online Tool | Prism',
    metaDescription: 'Count words, characters, lines, paragraphs, and sentences instantly. Estimates reading time. Fast, private, runs entirely in your browser.',
    keywords: ['word counter', 'word count online', 'character counter', 'reading time calculator', 'text statistics'],
    relatedTools: ['character-count', 'line-count', 'sort-lines'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [],

  async transform(input: string): Promise<string> {
    const text = input || '';

    const characters = text.length;
    const charactersNoSpaces = text.replace(/\s/g, '').length;
    const words = text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
    const lines = text === '' ? 0 : text.split('\n').length;
    const paragraphs = text.trim() === '' ? 0 : text.split(/\n\s*\n/).filter((p) => p.trim() !== '').length;
    const sentences = text.trim() === '' ? 0 : text.split(/[.!?]+/).filter((s) => s.trim() !== '').length;

    // Average reading speed: 238 words per minute
    const readingTimeMinutes = words / 238;
    const readingTime =
      readingTimeMinutes < 1
        ? 'Less than 1 minute'
        : `~${Math.ceil(readingTimeMinutes)} minute${Math.ceil(readingTimeMinutes) > 1 ? 's' : ''}`;

    return [
      `Words:                  ${words.toLocaleString()}`,
      `Characters:             ${characters.toLocaleString()}`,
      `Characters (no spaces): ${charactersNoSpaces.toLocaleString()}`,
      `Lines:                  ${lines.toLocaleString()}`,
      `Paragraphs:             ${paragraphs.toLocaleString()}`,
      `Sentences:              ${sentences.toLocaleString()}`,
      `Reading Time:           ${readingTime}`,
    ].join('\n');
  },
};
